/**
 * kpw-reviews.js  v4.1
 * KPW レビューシステム フロントエンドJS
 *
 * v4.1 変更点:
 *   - kpw-top-summary: テキストリンク → ボタンUI
 *     - 0件: [レビューを見る] + [✏ レビューを書く] の2ボタン表示
 *     - 有件: [レビューを見る（X件）] + [✏ レビューを書く] の2ボタン表示
 *
 * v4 変更点:
 *   - 取引レビュー（type=shop）対応
 *     - 3段階評価（😊良い/😐普通/😢気になる）= rating 5/3/1
 *     - renderTransactionRating / renderTransactionSummary
 *     - initTransactionRatingInput
 *
 * 設置: assets/kpw-reviews.js
 * ロード: layout/theme.liquid の </body> 直前に
 *   {{ 'kpw-reviews.js' | asset_url | script_tag }}
 */

(function () {
  'use strict';

  // ─────────────────────────────────────────
  // 定数
  // ─────────────────────────────────────────
  var STAR_D         = 'M10 1.5l2.47 5.01 5.53.81-4.01 3.9.95 5.53L10 14.27l-4.94 2.59.95-5.53-4.01-3.9 5.53-.81L10 1.5z';
  var LS_HELPFUL_PREFIX = 'kpw_h_';
  var PER_PAGE       = 5;
  var VOICE_COUNT    = 3;
  var _gradCount     = 0;
  var _productImageCache = {};

  // per-container ステート
  var _state = new WeakMap();
  function _getState(container) {
    if (!_state.has(container)) {
      var type = container.dataset.type || 'product';
      _state.set(container, {
        sort:    type === 'shop' ? 'new' : 'verified_first',
        page:    1,
        total:   0,
        loading: false,
        loaded:  false,
      });
    }
    return _state.get(container);
  }

  // ─────────────────────────────────────────
  // SVG 星
  // ─────────────────────────────────────────

  function _svgFull() {
    return '<svg class="kpw-star" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">'
      + '<path fill="#e8a000" d="' + STAR_D + '"/></svg>';
  }

  function _svgEmpty() {
    return '<svg class="kpw-star" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">'
      + '<path fill="#d4d4d4" d="' + STAR_D + '"/></svg>';
  }

  function _svgHalf() {
    var id = 'kpwhg' + (++_gradCount);
    return '<svg class="kpw-star" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">'
      + '<defs><linearGradient id="' + id + '">'
      + '<stop offset="50%" stop-color="#e8a000"/>'
      + '<stop offset="50%" stop-color="#d4d4d4"/>'
      + '</linearGradient></defs>'
      + '<path fill="url(#' + id + ')" d="' + STAR_D + '"/></svg>';
  }

  function renderStarsInt(rating) {
    var html = '<span class="kpw-stars" aria-label="' + rating + '点（5点満点）">';
    for (var i = 1; i <= 5; i++) {
      html += (i <= rating) ? _svgFull() : _svgEmpty();
    }
    return html + '</span>';
  }

  function renderStarsDecimal(avg) {
    var html = '<span class="kpw-stars" aria-label="平均' + avg.toFixed(1) + '点（5点満点）">';
    for (var i = 1; i <= 5; i++) {
      if (avg >= i) {
        html += _svgFull();
      } else if (avg >= i - 0.5) {
        html += _svgHalf();
      } else {
        html += _svgEmpty();
      }
    }
    return html + '</span>';
  }

  // ─────────────────────────────────────────
  // 取引レビュー: 顔SVGアイコン（赤/黄/青）
  // ─────────────────────────────────────────

  var _TXN = {
    good: {
      fill: '#E85555',
      mouth: '<path d="M13 25 Q20 31 27 25" stroke="#fff" stroke-width="2.5" fill="none" stroke-linecap="round"/>'
    },
    normal: {
      fill: '#F5B800',
      mouth: '<path d="M13 26 L27 26" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/>'
    },
    bad: {
      fill: '#4A82E4',
      mouth: '<path d="M13 28 Q20 22 27 28" stroke="#fff" stroke-width="2.5" fill="none" stroke-linecap="round"/>'
    }
  };

  function _txnFaceSvg(type, size) {
    var f = _TXN[type] || _TXN.normal;
    var s = size || 22;
    return '<svg class="kpw-txn-face" viewBox="0 0 40 40" width="' + s + '" height="' + s + '" aria-hidden="true">'
      + '<circle cx="20" cy="20" r="19" fill="' + f.fill + '"/>'
      + '<circle cx="13.5" cy="15" r="2.5" fill="#fff"/>'
      + '<circle cx="26.5" cy="15" r="2.5" fill="#fff"/>'
      + f.mouth + '</svg>';
  }

  function _txnType(rating) {
    return rating >= 4 ? 'good' : rating >= 2 ? 'normal' : 'bad';
  }

  function renderTransactionRating(rating) {
    var type  = _txnType(rating);
    var label = { good: '良い', normal: '普通', bad: '気になる' }[type];
    return '<span class="kpw-txn-badge kpw-txn-badge--' + type + '">'
      + _txnFaceSvg(type, 20)
      + '<span class="kpw-txn-badge__label">' + label + '</span>'
      + '</span>';
  }

  function renderTransactionSummary(data) {
    if (!data || data.review_count === 0) {
      return '<span class="kpw-txn-count">まだレビューがありません</span>';
    }
    var dist = data.rating_distribution || {};
    var good   = parseInt(dist['5'] || 0, 10);
    var normal = parseInt(dist['3'] || 0, 10);
    var bad    = parseInt(dist['1'] || 0, 10);
    return '<span class="kpw-txn-count kpw-txn-count--good">'   + _txnFaceSvg('good',   18) + ' ' + good   + '件</span>'
      + '<span class="kpw-txn-sep"> / </span>'
      + '<span class="kpw-txn-count kpw-txn-count--normal">' + _txnFaceSvg('normal', 18) + ' ' + normal + '件</span>'
      + '<span class="kpw-txn-sep"> / </span>'
      + '<span class="kpw-txn-count kpw-txn-count--bad">'    + _txnFaceSvg('bad',    18) + ' ' + bad    + '件</span>';
  }

  // ─────────────────────────────────────────
  // ユーティリティ
  // ─────────────────────────────────────────

  function formatDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    return d.getFullYear() + '/'
      + String(d.getMonth() + 1).padStart(2, '0') + '/'
      + String(d.getDate()).padStart(2, '0');
  }

  function escHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getApiUrl(container) {
    return (container.dataset.apiUrl || '').replace(/\/$/, '');
  }

  function _smoothScroll(selector) {
    var target = document.querySelector(selector);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ─────────────────────────────────────────
  // #kpw-top-summary 更新（商品タイトル直下）
  // ─────────────────────────────────────────

  function _updateTopSummary(data, productId) {
    var topEl = document.querySelector('#kpw-top-summary[data-product-id="' + productId + '"]');
    if (!topEl) return;
    var inner = topEl.querySelector('.kpw-top-summary__inner');
    if (!inner) return;

    var listTarget = topEl.dataset.listTarget || '#kpw-reviews-list';

    var viewLabel, viewTarget;
    var starsHtml;

    if (!data || data.review_count === 0) {
      starsHtml = renderStarsInt(0);
      viewLabel = 'レビューを見る';
      viewTarget = escHtml(listTarget);
      inner.innerHTML = starsHtml
        + '<span class="kpw-top-summary__zero-text">0件</span>'
        + '<button type="button" class="kpw-top-summary__btn kpw-top-summary__btn--view"'
        + ' data-scroll-to="' + viewTarget + '">' + viewLabel + '</button>'
        + '<button type="button" class="kpw-top-summary__btn kpw-top-summary__btn--write"'
        + ' data-scroll-to=".kpw-reviews-section">&#9998; レビューを書く</button>';
    } else {
      starsHtml = renderStarsDecimal(data.avg_rating);
      viewLabel = 'レビューを見る（' + data.review_count + '件）';
      viewTarget = escHtml(listTarget);
      inner.innerHTML = starsHtml
        + '<span class="kpw-top-summary__avg">' + data.avg_rating.toFixed(1) + '</span>'
        + '<button type="button" class="kpw-top-summary__btn kpw-top-summary__btn--view"'
        + ' data-scroll-to="' + viewTarget + '">' + viewLabel + '</button>'
        + '<button type="button" class="kpw-top-summary__btn kpw-top-summary__btn--write"'
        + ' data-scroll-to=".kpw-reviews-section">&#9998; レビューを書く</button>';
    }

    // クリック でスムーズスクロール（button は Enter/Space もブラウザが自動処理）
    inner.querySelectorAll('[data-scroll-to]').forEach(function (el) {
      el.addEventListener('click', function () {
        _smoothScroll(el.dataset.scrollTo);
      });
    });
  }

  // ─────────────────────────────────────────
  // 購入者の声 (voice summary)
  // ─────────────────────────────────────────

  function renderVoiceSummary(reviews) {
    var verified = reviews.filter(function (r) { return r.is_verified_purchase && r.body; });
    if (verified.length === 0) return '';

    var picks = verified.slice(0, VOICE_COUNT);
    var html  = '<div class="kpw-voice-summary">'
      + '<h4 class="kpw-voice-summary__title">購入者の評価まとめ</h4>'
      + '<div class="kpw-voice-summary__list">';

    picks.forEach(function (r) {
      var excerpt = r.body.length > 80 ? r.body.slice(0, 80) + '…' : r.body;
      html += '<div class="kpw-voice-summary__item">'
        + renderStarsInt(r.rating)
        + '<blockquote class="kpw-voice-summary__quote">' + escHtml(excerpt) + '</blockquote>'
        + '<p class="kpw-voice-summary__meta">' + escHtml(r.customer_name) + '</p>'
        + '</div>';
    });

    return html + '</div></div>';
  }

  // ─────────────────────────────────────────
  // レビューカード
  // ─────────────────────────────────────────

  function renderImages(images) {
    if (!images || images.length === 0) return '';
    var html = '<div class="kpw-review__images">';
    images.forEach(function (url) {
      if (!url) return;
      html += '<a class="kpw-review__image-link" href="' + escHtml(url)
        + '" target="_blank" rel="noopener noreferrer">'
        + '<img class="kpw-review__image" src="' + escHtml(url)
        + '" alt="レビュー画像" loading="lazy">'
        + '</a>';
    });
    return html + '</div>';
  }

  function renderReplies(replies) {
    if (!replies || replies.length === 0) return '';
    var html = '<div class="kpw-review__replies">';
    replies.forEach(function (r) {
      html += '<div class="kpw-review__reply">'
        + '<div class="kpw-review__reply-header">'
        + '<span class="kpw-review__reply-badge">店舗より</span>'
        + '<span class="kpw-review__reply-staff">' + escHtml(r.staff_name) + '</span>'
        + '<span class="kpw-review__date">' + formatDate(r.created_at) + '</span>'
        + '</div>'
        + '<p class="kpw-review__reply-body">' + escHtml(r.body) + '</p>'
        + '</div>';
    });
    return html + '</div>';
  }


  function reviewProductUrl(review) {
    return review && review.product_handle
      ? '/products/' + encodeURIComponent(review.product_handle)
      : '';
  }

  function renderProductThumb(review) {
    var productUrl = reviewProductUrl(review);
    var handle = review.product_handle || '';
    if (!productUrl || !handle) return '';
    return '<a class="kpw-review__product-thumb" href="' + escHtml(productUrl) + '" data-product-handle="' + escHtml(handle) + '" aria-label="商品ページ">'
      + '<span class="kpw-review__product-thumb-placeholder">&#21830;&#21697;</span>'
      + '</a>';
  }

  function productImageSrc(url) {
    if (!url) return '';
    return String(url).indexOf('?') === -1 ? url + '?width=160' : url + '&width=160';
  }

  function hydrateProductImages(root) {
    if (!root) return;
    var thumbs = root.querySelectorAll('.kpw-review__product-thumb[data-product-handle]:not([data-loaded])');
    thumbs.forEach(function (thumb) {
      var handle = thumb.getAttribute('data-product-handle');
      if (!handle) return;
      thumb.setAttribute('data-loaded', '1');

      function applyImage(url) {
        if (!url) return;
        thumb.innerHTML = '<img src="' + escHtml(productImageSrc(url)) + '" alt="" loading="lazy">';
      }

      if (_productImageCache[handle]) {
        applyImage(_productImageCache[handle]);
        return;
      }

      fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (product) {
          if (!product) return;
          var image = product.featured_image || (product.images && product.images[0]) || '';
          if (!image) return;
          _productImageCache[handle] = image;
          applyImage(image);
        })
        .catch(function () {});
    });
  }

  function handleToTitle(handle) {
    if (!handle) return '';
    return String(handle)
      .replace(/-/g, ' ')
      .replace(/\b\w/g, function (m) { return m.toUpperCase(); });
  }

  function renderReviewTypeMeta(review, effectiveType) {
    if (effectiveType === 'shop') {
      return '<div class="kpw-review__target">'
        + '<span class="kpw-review__type-badge kpw-review__type-badge--shop">&#21462;&#24341;&#12524;&#12499;&#12517;&#12540;</span>'
        + '<span class="kpw-review__target-name">KPW&#20844;&#24335;&#12458;&#12531;&#12521;&#12452;&#12531;&#12471;&#12519;&#12483;&#12503;</span>'
        + '</div>';
    }

    var handle = review.product_handle || '';
    var productName = handleToTitle(handle) || '&#21830;&#21697;&#12524;&#12499;&#12517;&#12540;';
    var productUrl = reviewProductUrl(review);
    return '<div class="kpw-review__target kpw-review__target--product">'
      + renderProductThumb(review)
      + '<div class="kpw-review__target-main">'
      + '<span class="kpw-review__type-badge kpw-review__type-badge--product">&#21830;&#21697;&#12524;&#12499;&#12517;&#12540;</span>'
      + '<span class="kpw-review__target-name">' + escHtml(productName) + '</span>'
      + (productUrl
        ? '<a class="kpw-review__product-link" href="' + escHtml(productUrl) + '">&#21830;&#21697;&#12506;&#12540;&#12472;</a>'
        : '')
      + '</div>'
      + '</div>';
  }
  function renderReview(review, containerType) {
    var reviewId      = review.id;
    var effectiveType = review.type || containerType;
    var alreadyHelped = !!localStorage.getItem(LS_HELPFUL_PREFIX + reviewId);
    var helpfulCount  = review.helpful_count || 0;
    var verifiedBadge = review.is_verified_purchase
      ? '<span class="kpw-review__verified">購入済み</span>' : '';
    var helpfulBtn = '<button type="button"'
      + ' class="kpw-helpful-btn' + (alreadyHelped ? ' is-helped' : '') + '"'
      + ' data-review-id="' + reviewId + '"'
      + (alreadyHelped ? ' disabled aria-disabled="true"' : '')
      + '>'
      + (alreadyHelped ? '<span class="kpw-helpful-btn__check">✓</span> 参考になった' : '参考になった')
      + ' <span class="kpw-helpful-btn__count">(' + helpfulCount + ')</span>'
      + '</button>';

    var ratingHtml = effectiveType === 'shop'
      ? renderTransactionRating(review.rating)
      : renderStarsInt(review.rating);

    return '<div class="kpw-review' + (review.is_verified_purchase ? ' is-verified' : '') + '" data-id="' + reviewId + '">'
      + '<div class="kpw-review__header">'
      + ratingHtml
      + (review.title ? '<span class="kpw-review__title">' + escHtml(review.title) + '</span>' : '')
      + verifiedBadge
      + '<span class="kpw-review__name">' + escHtml(review.customer_name) + '</span>'
      + '<span class="kpw-review__date">' + formatDate(review.created_at) + '</span>'
      + '</div>'
      + renderReviewTypeMeta(review, effectiveType)
      + '<div class="kpw-review__body">' + escHtml(review.body) + '</div>'
      + renderImages(review.images)
      + '<div class="kpw-review__footer">' + helpfulBtn + '</div>'
      + renderReplies(review.replies)
      + '</div>';
  }

  // ─────────────────────────────────────────
  // ソートバー（product typeのみ）
  // ─────────────────────────────────────────

  var SORT_OPTIONS = [
    { key: 'verified_first', label: 'おすすめ' },
    { key: 'new',            label: '新着' },
    { key: 'rating_desc',    label: '評価高い' },
    { key: 'rating_asc',     label: '評価低い' },
  ];

  function renderSortBar(currentSort) {
    var html = '<div class="kpw-sort-bar" role="group" aria-label="並び替え">';
    SORT_OPTIONS.forEach(function (s) {
      var active = currentSort === s.key;
      html += '<button type="button"'
        + ' class="kpw-sort-btn' + (active ? ' is-active' : '') + '"'
        + ' data-sort="' + s.key + '"'
        + (active ? ' aria-current="true"' : '')
        + '>' + s.label + '</button>';
    });
    return html + '</div>';
  }

  // ─────────────────────────────────────────
  // API: サマリー読み込み
  // ─────────────────────────────────────────

  function loadSummary(container, apiUrl) {
    var type      = container.dataset.type;
    var productId = container.dataset.productId || '';
    if (type === 'all') {
      fetch(apiUrl + '/reviews?type=all&sort=new&per_page=1&page=1')
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          var summaryEl = container.querySelector('.kpw-txn-summary');
          if (summaryEl && data) {
            summaryEl.innerHTML = '<span class=kpw-txn-count>&#12524;&#12499;&#12517;&#12540; ' + (data.total || 0) + '&#20214;</span>';
          }
        })
        .catch(function (err) {
          console.error('[KPWReviews] summary load error:', err);
        });
      return;
    }
    var q = type === 'shop'
      ? 'type=shop'
      : 'product_id=' + encodeURIComponent(productId);

    fetch(apiUrl + '/reviews/summary?' + q)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data) return;

        if (type === 'shop') {
          // 取引レビュー: 絵文字サマリー更新
          var txnSummaryEl = container.querySelector('.kpw-txn-summary');
          if (txnSummaryEl) {
            txnSummaryEl.innerHTML = renderTransactionSummary(data);
          }
        } else {
          // 商品レビュー: 星サマリー更新
          var summaryEl = container.querySelector('.kpw-reviews-summary');
          if (summaryEl) {
            var starsEl = summaryEl.querySelector('.kpw-reviews-summary__stars');
            var avgEl   = summaryEl.querySelector('.kpw-reviews-summary__avg');
            var countEl = summaryEl.querySelector('.kpw-reviews-summary__count-link, .kpw-reviews-summary__count');

            if (starsEl) starsEl.innerHTML = renderStarsDecimal(data.review_count > 0 ? data.avg_rating : 0);
            if (avgEl)   avgEl.textContent  = data.review_count > 0 ? data.avg_rating.toFixed(1) : '-';
            if (countEl) countEl.textContent = data.review_count > 0
              ? data.review_count + '件のレビュー'
              : 'まだレビューがありません';
          }

          // #kpw-top-summary 更新（商品レビューのみ）
          _updateTopSummary(data, productId);
        }
      })
      .catch(function (err) {
        console.error('[KPWReviews] summary load error:', err);
      });
  }

  // ─────────────────────────────────────────
  // API: レビュー一覧読み込み（append対応）
  // ─────────────────────────────────────────

  function loadReviews(container, apiUrl, sort, page, append) {
    var st = _getState(container);
    if (st.loading) return;

    sort   = sort || st.sort;
    page   = page || 1;
    append = !!append;

    st.sort    = sort;
    st.page    = page;
    st.loading = true;

    var type     = container.dataset.type;
    var listEl   = container.querySelector('.kpw-reviews-list');
    var sortWrap = container.querySelector('.kpw-sort-wrap');
    if (!listEl || !apiUrl) { st.loading = false; return; }

    // ソートバー: product typeのみ表示
    if (sortWrap) {
      sortWrap.innerHTML = type === 'shop' ? '' : renderSortBar(sort);
    }

    if (!append) {
      listEl.innerHTML = '<p class="kpw-reviews-list__loading">読み込み中...</p>';
    }

    var base = type === 'shop'
      ? 'type=shop'
      : (type === 'all'
        ? 'type=all'
        : 'product_id=' + encodeURIComponent(container.dataset.productId || ''));
    var q = base + '&sort=' + sort + '&per_page=' + PER_PAGE + '&page=' + page;

    fetch(apiUrl + '/reviews?' + q)
      .then(function (res) {
        if (!res.ok) throw new Error('API error ' + res.status);
        return res.json();
      })
      .then(function (data) {
        st.loading = false;
        st.total   = data.total || 0;

        var reviews = data.reviews || [];

        if (!append && reviews.length === 0) {
          var emptyIcon = type === 'shop'
            ? '<svg viewBox="0 0 40 40" width="32" height="32" aria-hidden="true"><circle cx="20" cy="20" r="19" fill="#E85555"/><circle cx="13.5" cy="15" r="2.5" fill="#fff"/><circle cx="26.5" cy="15" r="2.5" fill="#fff"/><path d="M13 25 Q20 31 27 25" stroke="#fff" stroke-width="2.5" fill="none" stroke-linecap="round"/></svg>'
            : '&#9733;';
          var emptyMsg  = type === 'shop' ? 'まだ取引レビューがありません' : 'まだレビューがありません';
          var emptySub  = type === 'shop'
            ? '最初の取引レビューを投稿してください。'
            : '最初のレビューを投稿してください。お客様の声がほかのお客様の参考になります。';
          listEl.innerHTML = '<div class="kpw-reviews-list__empty">'
            + '<p style="font-size:32px;margin-bottom:8px;">' + emptyIcon + '</p>'
            + '<p style="font-weight:700;font-size:15px;color:#333;margin-bottom:8px;">' + emptyMsg + '</p>'
            + '<p style="font-size:13px;color:#888;">' + emptySub + '</p>'
            + '</div>';
          return;
        }

        if (!append) {
          listEl.innerHTML = '';
          // 購入者の声（verified_first 初回のみ、product typeのみ）
          if (type !== 'shop' && sort === 'verified_first' && page === 1) {
            var voiceContainer = container.querySelector('.kpw-voice-container');
            if (voiceContainer && !voiceContainer.dataset.populated) {
              var voiceHtml = renderVoiceSummary(reviews);
              if (voiceHtml) {
                voiceContainer.innerHTML = voiceHtml;
                voiceContainer.dataset.populated = '1';
              }
            }
          }
        }

        // 既存の「もっと見る」ボタンを除去
        var oldMore = listEl.querySelector('.kpw-load-more-wrap');
        if (oldMore) oldMore.remove();

        // レビューカードを追記
        var reviewsHtml = '';
        reviews.forEach(function (r) { reviewsHtml += renderReview(r, type); });
        listEl.insertAdjacentHTML('beforeend', reviewsHtml);
        hydrateProductImages(listEl);

        // 「もっと見る」ボタン
        if (st.total > page * PER_PAGE) {
          listEl.insertAdjacentHTML('beforeend',
            '<div class="kpw-load-more-wrap">'
            + '<button type="button" class="kpw-load-more-btn">'
            + 'もっと見る（残り ' + (st.total - page * PER_PAGE) + '件）'
            + '</button></div>'
          );
        }

        st.loaded = true;
      })
      .catch(function (err) {
        st.loading = false;
        console.error('[KPWReviews] reviews load error:', err);
        if (!append) {
          listEl.innerHTML = '<p class="kpw-reviews-list__error">レビューの読み込みに失敗しました。</p>';
        }
      });
  }

  // ─────────────────────────────────────────
  // 参考になった ボタン（イベント委譲 — init時1回のみ登録）
  // ─────────────────────────────────────────

  function _bindHelpfulButtons(listEl, apiUrl) {
    listEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.kpw-helpful-btn:not(.is-helped):not([disabled])');
      if (!btn) return;
      var reviewId = btn.dataset.reviewId;
      if (!reviewId) return;
      if (localStorage.getItem(LS_HELPFUL_PREFIX + reviewId)) {
        btn.classList.add('is-helped');
        btn.disabled = true;
        return;
      }
      btn.disabled = true;
      btn.classList.add('is-loading');
      fetch(apiUrl + '/reviews/' + reviewId + '/helpful', { method: 'POST' })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data.ok) {
            localStorage.setItem(LS_HELPFUL_PREFIX + reviewId, '1');
            btn.classList.remove('is-loading');
            btn.classList.add('is-helped');
            btn.innerHTML = '<span class="kpw-helpful-btn__check">✓</span> 参考になった'
              + ' <span class="kpw-helpful-btn__count">(' + data.helpful_count + ')</span>';
            btn.setAttribute('aria-disabled', 'true');
          } else {
            btn.disabled = false;
            btn.classList.remove('is-loading');
          }
        })
        .catch(function (err) {
          console.error('[KPWReviews] helpful error:', err);
          btn.disabled = false;
          btn.classList.remove('is-loading');
        });
    });
  }

  // ─────────────────────────────────────────
  // ソートボタン（イベント委譲）
  // ─────────────────────────────────────────

  function _bindSortButtons(container, apiUrl) {
    var sortWrap = container.querySelector('.kpw-sort-wrap');
    if (!sortWrap) return;
    sortWrap.addEventListener('click', function (e) {
      var btn = e.target.closest('.kpw-sort-btn');
      if (!btn) return;
      var sort = btn.dataset.sort;
      if (!sort) return;
      loadReviews(container, apiUrl, sort, 1, false);
    });
  }

  // ─────────────────────────────────────────
  // もっと見るボタン（イベント委譲）
  // ─────────────────────────────────────────

  function _bindLoadMore(container, apiUrl) {
    var listEl = container.querySelector('.kpw-reviews-list');
    if (!listEl) return;
    listEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.kpw-load-more-btn');
      if (!btn) return;
      var st = _getState(container);
      if (st.loading) return;
      btn.disabled = true;
      btn.textContent = '読み込み中...';
      loadReviews(container, apiUrl, st.sort, st.page + 1, true);
    });
  }

  // ─────────────────────────────────────────
  // IntersectionObserver: レビュー遅延ロード
  // ─────────────────────────────────────────

  function _lazyLoadReviews(container, apiUrl) {
    var listEl = container.querySelector('.kpw-reviews-list');
    if (!listEl) return;

    var st = _getState(container);

    if (!('IntersectionObserver' in window)) {
      loadReviews(container, apiUrl, st.sort, 1, false);
      return;
    }

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          obs.unobserve(entry.target);
          var s = _getState(container);
          if (!s.loaded && !s.loading) {
            loadReviews(container, apiUrl, s.sort, 1, false);
          }
        }
      });
    }, { rootMargin: '200px' });

    observer.observe(listEl);
  }

  // ─────────────────────────────────────────
  // 星評価入力（product type）
  // ─────────────────────────────────────────

  function _updateStarInput(stars, value) {
    stars.forEach(function (s) {
      var active = parseInt(s.dataset.value) <= value;
      s.classList.toggle('is-active', active);
      var path = s.querySelector('path');
      if (path) path.setAttribute('fill', active ? '#e8a000' : '#d4d4d4');
    });
  }

  function initStarInput(form) {
    var container = form.querySelector('.kpw-star-input');
    if (!container) return;
    var stars       = Array.from(container.querySelectorAll('.kpw-star-input__btn'));
    var hiddenInput = form.querySelector('input[name="rating"]');

    container.addEventListener('click', function (e) {
      var btn = e.target.closest('.kpw-star-input__btn');
      if (!btn) return;
      var val = parseInt(btn.dataset.value);
      if (hiddenInput) hiddenInput.value = val;
      _updateStarInput(stars, val);
    });
    container.addEventListener('mouseover', function (e) {
      var btn = e.target.closest('.kpw-star-input__btn');
      if (!btn) return;
      _updateStarInput(stars, parseInt(btn.dataset.value));
    });
    container.addEventListener('mouseout', function () {
      var current = hiddenInput ? parseInt(hiddenInput.value) || 0 : 0;
      _updateStarInput(stars, current);
    });
  }

  // ─────────────────────────────────────────
  // 3段階評価入力（shop type）
  // ─────────────────────────────────────────

  function initTransactionRatingInput(form) {
    var ratingContainer = form.querySelector('.kpw-txn-rating');
    if (!ratingContainer) return;
    var hiddenInput = form.querySelector('input[name="rating"]');
    var btns = Array.from(ratingContainer.querySelectorAll('.kpw-txn-btn'));

    ratingContainer.addEventListener('click', function (e) {
      var btn = e.target.closest('.kpw-txn-btn');
      if (!btn) return;
      var val = btn.dataset.value;
      if (hiddenInput) hiddenInput.value = val;
      btns.forEach(function (b) { b.classList.toggle('is-active', b === btn); });
    });
  }

  // ─────────────────────────────────────────
  // URLパラメータ pre-fill
  // ─────────────────────────────────────────

  function prefillFromUrl(form) {
    var params     = new URLSearchParams(window.location.search);
    var orderId    = params.get('review_order_id');
    var lineItemId = params.get('review_line_item_id');
    if (orderId) {
      var orderInput = form.querySelector('input[name="order_id"]');
      if (orderInput) orderInput.value = orderId;
    }
    if (lineItemId) {
      var liInput = form.querySelector('input[name="order_line_item_id"]');
      if (liInput) liInput.value = lineItemId;
    }
  }

  // ─────────────────────────────────────────
  // 画像プレビュー
  // ─────────────────────────────────────────

  function initImagePreviews(form) {
    var inputs  = form.querySelectorAll('.kpw-image-url-input');
    var preview = form.querySelector('.kpw-image-preview');
    if (!preview) return;
    function updatePreview() {
      preview.innerHTML = '';
      inputs.forEach(function (inp) {
        var url = inp.value.trim();
        if (!url) return;
        var img = document.createElement('img');
        img.src = url;
        img.alt = '画像プレビュー';
        img.className = 'kpw-image-preview__img';
        img.onerror = function () { img.style.display = 'none'; };
        preview.appendChild(img);
      });
    }
    inputs.forEach(function (inp) { inp.addEventListener('input', updatePreview); });
  }

  // ─────────────────────────────────────────
  // バリデーション
  // ─────────────────────────────────────────

  function validatePayload(payload, type) {
    if (!payload.rating || parseInt(payload.rating) < 1) {
      return type === 'shop' ? '評価を選択してください' : '評価（星）を選択してください';
    }
    if (!payload.body || !payload.body.trim()) return 'レビュー本文を入力してください';
    if (!payload.customer_name || !payload.customer_name.trim()) return 'お名前を入力してください';
    if (type === 'product') {
      if (!payload.order_id || !payload.order_id.trim()) return '注文番号を入力してください（購入者レビュー認定に必要です）';
      if (!payload.order_line_item_id || !payload.order_line_item_id.trim()) return 'URL に ?review_order_id=XXX&review_line_item_id=YYY を付けてアクセスしてください';
    }
    return null;
  }

  // ─────────────────────────────────────────
  // フォーム送信
  // ─────────────────────────────────────────

  function fetchReviewsPrincipal() {
    return fetch('/apps/kpw-inbox/reviews/auth/token', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Accept': 'application/json' }
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || !data || !data.access_token) {
          throw new Error(data && data.error === 'login_required' ? 'login_required' : 'auth_unavailable');
        }
        return data.access_token;
      });
    });
  }

  function bindFormSubmit(form, apiUrl, container) {
    var submitBtn    = form.querySelector('[type="submit"]');
    var errorEl      = form.querySelector('.kpw-form-error');
    var isSubmitting = false;
    var type         = container.dataset.type;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (isSubmitting) return;

      var formData = new FormData(form);
      var payload  = {};
      formData.forEach(function (val, key) { payload[key] = val; });

      var imageUrls = [];
      form.querySelectorAll('.kpw-image-url-input').forEach(function (inp) {
        var url = inp.value.trim();
        if (url) imageUrls.push(url);
      });
      if (imageUrls.length > 0) payload.images = imageUrls;

      var errMsg = validatePayload(payload, payload.type);
      if (errMsg) {
        if (errorEl) errorEl.textContent = errMsg;
        return;
      }
      if (errorEl) errorEl.textContent = '';

      isSubmitting = true;
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = '送信中...'; }

      fetchReviewsPrincipal()
        .then(function (accessToken) {
          return fetch(apiUrl + '/reviews', {
            method:  'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + accessToken
            },
            body: JSON.stringify(payload),
          });
        })
        .then(function (res) {
          return res.json().then(function (data) { return { status: res.status, data: data }; });
        })
        .then(function (result) {
          if (result.status === 201) {
            var wrap = form.closest('.kpw-review-form-wrap');
            if (wrap) {
              var rev = result.data && result.data.review;
              var pts = rev && rev.points_granted_count;
              var isFirst = rev && rev.is_first_review;
              var pointsLine = '';
              if (pts) {
                pointsLine = isFirst
                  ? '<br><span style="color:#b8860b;font-weight:700;">&#9733; 初回ボーナスで ' + pts + 'ポイント付与しました！</span>'
                  : '<br><span style="color:#b8860b;font-weight:700;">&#9733; ' + pts + 'ポイント付与しました！</span>';
              }
              var subMsg = type === 'shop'
                ? '商品レビューも投稿するとポイントが貯まります ›'
                : '他の商品もレビューするとポイントが貯まります ›';
              wrap.innerHTML = '<div class="kpw-success-msg">'
                + '<strong>レビューありがとうございます！</strong>'
                + pointsLine
                + '<br><span class="kpw-success-msg__sub">' + subMsg + '</span>'
                + '</div>';
            }
            loadSummary(container, apiUrl);
            loadReviews(container, apiUrl, _getState(container).sort, 1, false);
          } else {
            var msg = (result.data && (result.data.error || result.data.details)) || '送信に失敗しました';
            if (Array.isArray(msg)) msg = msg.join(' / ');
            if (errorEl) errorEl.textContent = msg;
            isSubmitting = false;
            if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'レビューを投稿する'; }
          }
        })
        .catch(function (err) {
          console.error('[KPWReviews] submit error:', err);
          if (errorEl) {
            errorEl.textContent = err && err.message === 'login_required'
              ? 'Shopifyアカウントへログインしてから投稿してください。'
              : '購入者確認に失敗しました。時間をおいて再度お試しください。';
          }
          isSubmitting = false;
          if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'レビューを投稿する'; }
        });
    });
  }

  // ─────────────────────────────────────────
  // 公開 API
  // ─────────────────────────────────────────

  window.KPWReviews = {
    /**
     * 単一コンテナを初期化
     * @param {Element} container data-api-url を持つ要素
     */
    init: function (container) {
      if (!container) return;
      var apiUrl = getApiUrl(container);
      if (!apiUrl) {
        console.warn('[KPWReviews] data-api-url が設定されていません');
        return;
      }

      var type = container.dataset.type;

      // ① サマリー即時取得
      loadSummary(container, apiUrl);

      // ② 参考になったボタン（イベント委譲を1回だけ登録）
      var listEl = container.querySelector('.kpw-reviews-list');
      if (listEl) _bindHelpfulButtons(listEl, apiUrl);

      // ③ ソートボタン（イベント委譲）
      _bindSortButtons(container, apiUrl);

      // ④ もっと見るボタン（イベント委譲）
      _bindLoadMore(container, apiUrl);

      // ⑤ レビュー一覧: IntersectionObserver で遅延ロード
      _lazyLoadReviews(container, apiUrl);

      // ⑥ フォーム
      var form = container.querySelector('.kpw-review-form');
      if (form) {
        var formTypeInput = form.querySelector('input[name=type]');
        var formType = formTypeInput && formTypeInput.value ? formTypeInput.value : type;
        if (formType === 'shop') {
          // 取引レビュー: フォームは常時表示、URLからorder_idをprefill
          var params = new URLSearchParams(window.location.search);
          var orderIdFromUrl = params.get('review_order_id');
          if (orderIdFromUrl) {
            var orderInput = form.querySelector('input[name="order_id"]');
            if (orderInput) orderInput.value = orderIdFromUrl;
          }
          initTransactionRatingInput(form);
        } else {
          // 商品レビュー: URLパラメータがある場合のみフォームを表示
          var reviewParams = new URLSearchParams(window.location.search);
          var hasReviewParams = reviewParams.get('review_order_id') && reviewParams.get('review_line_item_id');
          var formWrap   = container.querySelector('.kpw-review-form-wrap');
          var buyNotice  = container.querySelector('.kpw-purchase-required-notice');
          if (hasReviewParams) {
            if (formWrap)  formWrap.style.display  = '';
            if (buyNotice) buyNotice.style.display = 'none';
            prefillFromUrl(form);
          }
          initStarInput(form);
          initImagePreviews(form);
        }
        bindFormSubmit(form, apiUrl, container);
      }
    },

    /** ページ内の全コンテナを自動初期化 */
    initAll: function () {
      var self = this;
      document.querySelectorAll('.kpw-reviews-section[data-api-url]').forEach(function (el) {
        self.init(el);
      });
    },
  };

  // DOMContentLoaded で自動初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.KPWReviews.initAll(); });
  } else {
    window.KPWReviews.initAll();
  }
})();
